import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import Providers from './pages/Providers';
import Clients from './pages/Clients';
import Apps from './pages/Apps';
import AppConfig from './pages/AppConfig';
import APIKeys from './pages/APIKeys';
import SecurityCentre from './pages/SecurityCentre';
import Deliverability from './pages/Deliverability';
import EmailPacks from './pages/EmailPacks';
import SecureVault from './pages/SecureVault';
import Templates from './pages/Templates';
import OTPSettings from './pages/OTPSettings';
import ScheduledEmails from './pages/ScheduledEmails';
import Logs from './pages/Logs';
import Audit from './pages/Audit';
import Usage from './pages/Usage';
import SuppressionList from './pages/SuppressionList';
import AIAssistant from './pages/AIAssistant';
import Settings from './pages/Settings';
import DataModel from './pages/DataModel';
import GenericPage from './pages/GenericPage';
import { navigationConfig } from './config/navigation';
import SafeIcon from './common/SafeIcon';
import { FiLock, FiShield, FiLoader, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import './App.css';

// PB-11A: Admin Login Gate Implementation
const AdminLoginGate = ({ onLoginSuccess }) => {
  const [passcode, setPasscode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passcode) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'admin_login', payload: { passcode } })
      });

      // Handle specific HTTP statuses before parsing JSON
      if (res.status === 404) {
        setError("Gateway function not found. Check Netlify Functions deployment.");
        setLoading(false);
        return;
      }

      let result;
      try {
        result = await res.json();
      } catch (parseErr) {
        setError("Gateway returned a non-JSON response. Check Netlify function deployment.");
        setLoading(false);
        return;
      }

      if (result.success) {
        sessionStorage.setItem('bpa_gateway_admin_session', 'active');
        sessionStorage.setItem('bpa_gateway_admin_passcode', passcode);
        onLoginSuccess();
      } else {
        setError(result.message || 'Invalid passcode');
      }
    } catch (err) {
      setError('Unable to reach gateway endpoint. Check deployment or endpoint path.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Background Polish */}
      <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-emerald-600 rounded-full blur-[120px]"></div>
      </div>

      <div className="w-full max-w-sm z-10 space-y-8">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 bg-blue-600 rounded-3xl flex items-center justify-center text-white font-black text-4xl shadow-2xl shadow-blue-900/50 mx-auto">
            B
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Admin Access Required</h1>
            <p className="text-slate-400 text-sm mt-1">Secure access to BPA Email Gateway</p>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-3xl shadow-3xl backdrop-blur-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1 flex items-center gap-2">
                <FiLock /> Admin Passcode
              </label>
              <input
                autoFocus
                type="password"
                placeholder="••••••••"
                className="w-full px-5 py-3.5 bg-slate-800 border border-slate-700 rounded-2xl text-white outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all text-center tracking-[0.5em] font-black"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
              />
            </div>
            
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-2 text-red-400 text-xs font-bold animate-in fade-in zoom-in-95">
                <FiAlertCircle /> {error}
              </div>
            )}

            <button
              disabled={loading || !passcode}
              className="w-full py-4 bg-blue-600 text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-900/20 hover:bg-blue-500 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <FiLoader className="animate-spin" /> : <FiShield />}
              Authenticate Session
            </button>
          </form>
        </div>

        <p className="text-center text-slate-600 text-[10px] font-bold uppercase tracking-widest">
          BPA Solutions Internal Infrastructure
        </p>
      </div>
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    sessionStorage.getItem('bpa_gateway_admin_session') === 'active'
  );

  // PB-11A: Global Fetch Interceptor for Administrative Passcode
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const [url, options = {}] = args;
      
      // Inject header for gateway calls if session exists
      if (typeof url === 'string' && url.includes('/.netlify/functions/gateway')) {
        const passcode = sessionStorage.getItem('bpa_gateway_admin_passcode');
        if (passcode) {
          options.headers = {
            ...options.headers,
            'x-bpa-admin-passcode': passcode
          };
        }
      }

      const response = await originalFetch(url, options);

      // PB-11A: Handle Session Expiration/Invalidation
      if (response.status === 401 || response.status === 403) {
        const clonedRes = response.clone();
        try {
          const body = await clonedRes.json();
          if (body.errorCode === 'ADMIN_AUTH_REQUIRED' || body.errorCode === 'INVALID_ADMIN_PASSCODE') {
            sessionStorage.clear();
            setIsAuthenticated(false);
            window.dispatchEvent(new CustomEvent('show-toast', { 
              detail: { message: 'Admin session expired or invalid. Please log in again.', type: 'error' } 
            }));
          }
        } catch (e) {
          // Not a JSON response or different error
        }
      }
      return response;
    };

    // PB-11A: Listen for Logout Event
    const handleLogout = () => {
      sessionStorage.clear();
      setIsAuthenticated(false);
    };
    window.addEventListener('bpa-logout', handleLogout);

    return () => {
      window.fetch = originalFetch;
      window.removeEventListener('bpa-logout', handleLogout);
    };
  }, []);

  const implementedPaths = [
    '/', '/clients', '/apps', '/app-config', '/providers', '/api-keys',
    '/security', '/deliverability', '/email-packs', '/secure-vault',
    '/templates', '/otp-settings', '/scheduled', '/logs', '/audit',
    '/usage', '/suppression', '/ai-assistant', '/settings', '/data-model'
  ];

  if (!isAuthenticated) {
    return <AdminLoginGate onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  return (
    <ErrorBoundary>
      <HashRouter>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path="clients" element={<Clients />} />
            <Route path="apps" element={<Apps />} />
            <Route path="app-config" element={<AppConfig />} />
            <Route path="providers" element={<Providers />} />
            <Route path="api-keys" element={<APIKeys />} />
            <Route path="security" element={<SecurityCentre />} />
            <Route path="deliverability" element={<Deliverability />} />
            <Route path="email-packs" element={<EmailPacks />} />
            <Route path="secure-vault" element={<SecureVault />} />
            <Route path="templates" element={<Templates />} />
            <Route path="otp-settings" element={<OTPSettings />} />
            <Route path="scheduled" element={<ScheduledEmails />} />
            <Route path="logs" element={<Logs />} />
            <Route path="audit" element={<Audit />} />
            <Route path="usage" element={<Usage />} />
            <Route path="suppression" element={<SuppressionList />} />
            <Route path="ai-assistant" element={<AIAssistant />} />
            <Route path="settings" element={<Settings />} />
            <Route path="data-model" element={<DataModel />} />
            {navigationConfig
              .filter(nav => !implementedPaths.includes(nav.path))
              .map((nav) => (
                <Route
                  key={nav.path}
                  path={nav.path.replace('/', '')}
                  element={<GenericPage title={nav.name} />}
                />
              ))}
            <Route path="*" element={<GenericPage title="Page Not Found" />} />
          </Route>
        </Routes>
      </HashRouter>
    </ErrorBoundary>
  );
}

export default App;